import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button.js";
import { getSession } from "@/lib/session.js";
import { inviteMember, listMembers, removeMember } from "@/lib/license-api.js";
import type { TeamMemberSummary } from "@agencylens/shared";

/**
 * Settings → Team → Members (Tier 3 only).
 *
 * A Tier-3 owner invites teammates by email; each gets their own login under
 * the agency's license, inheriting the owner's tier. Members work in their own
 * encrypted workspace (own connections/reports) — shared-account data is a
 * planned follow-up. The server enforces the Tier-3 gate; this panel mirrors it.
 */
export function TeamPanel({
  onError,
}: {
  onError: (msg: string | undefined) => void;
}) {
  const session = getSession();
  const tier = session?.payload.tier;
  const role = session?.payload.role;
  const tierIsThree = tier === "tier-3";
  const isOwner = role !== "member";

  const [members, setMembers] = useState<TeamMemberSummary[]>([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();

  const refresh = useCallback(async () => {
    if (!session || !tierIsThree) return;
    try {
      const r = await listMembers(session.jwt);
      setMembers(r.members);
    } catch (e) {
      const ee = e as Error & { error?: string };
      if (ee.error !== "tier_too_low") onError(`Couldn't load team: ${ee.error ?? ee.message}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.jwt, tierIsThree, onError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!session) return;
    const target = email.trim().toLowerCase();
    if (!target) return;
    onError(undefined);
    setNotice(undefined);
    setBusy("Sending invite…");
    try {
      const r = await inviteMember(session.jwt, target);
      setEmail("");
      setNotice(
        r.sent
          ? `Invite sent to ${target}.`
          : `Invite created for ${target}${r.devInviteLink ? ` — dev link: ${r.devInviteLink}` : ""}.`,
      );
      await refresh();
    } catch (e) {
      onError(inviteError(e as Error & { error?: string }));
    } finally {
      setBusy(undefined);
    }
  }

  async function onRemove(target: string) {
    if (!session) return;
    if (!window.confirm(`Remove ${target} from your team?`)) return;
    onError(undefined);
    setBusy(`Removing ${target}…`);
    try {
      await removeMember(session.jwt, target);
      await refresh();
    } catch (e) {
      const ee = e as Error & { error?: string };
      onError(`Remove failed: ${ee.error ?? ee.message}`);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="mb-2 text-lg font-medium">Team members</h2>
      <p className="mb-4 text-sm text-slate-600">
        Tier 3 lets you invite teammates. Each gets their own sign-in under your
        agency license. Invites expire after 7 days.
      </p>

      {!tierIsThree && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Team seats are a <strong>Tier 3</strong> feature. Your current tier is{" "}
          <strong>{tier ?? "unknown"}</strong>.
        </div>
      )}

      {tierIsThree && !isOwner && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          You're a team member on this account. Only the account owner can invite
          or remove members.
        </div>
      )}

      {tierIsThree && isOwner && (
        <>
          <form onSubmit={onInvite} className="mb-4 flex flex-wrap items-end gap-2">
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Invite by email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@agency.com"
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
            <Button type="submit" disabled={busy !== undefined}>
              Send invite
            </Button>
          </form>

          {notice && (
            <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              {notice}
            </div>
          )}

          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
            {members.map((m) => (
              <li
                key={`${m.email}-${m.status}`}
                className="flex items-center justify-between px-3 py-2 text-sm"
              >
                <span className="flex items-center gap-2">
                  <span className="text-slate-800">{m.email}</span>
                  {m.role === "owner" && (
                    <span className="rounded bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-brand-700">
                      owner
                    </span>
                  )}
                  {m.status === "pending" && (
                    <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                      pending
                    </span>
                  )}
                </span>
                {m.role !== "owner" && (
                  <button
                    type="button"
                    onClick={() => void onRemove(m.email)}
                    disabled={busy !== undefined}
                    className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    {m.status === "pending" ? "Revoke" : "Remove"}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {busy && <p className="mt-2 text-sm text-slate-500">{busy}</p>}
        </>
      )}
    </section>
  );
}

function inviteError(e: Error & { error?: string }): string {
  switch (e.error) {
    case "already_member":
      return "That email is already a member of your team.";
    case "email_unavailable":
      return "That email already has its own AgencyLens account.";
    case "cannot_invite_self":
      return "You can't invite your own email.";
    case "invalid_email":
      return "That doesn't look like a valid email address.";
    case "tier_too_low":
      return "Team seats are a Tier 3 feature.";
    case "email_send_failed":
      return "Couldn't send the invite email. Try again shortly.";
    default:
      return `Invite failed: ${e.error ?? e.message}`;
  }
}
